import React from "react";
import TipJarRepository from "../../Repositories/TipJarRepository";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import dayjs from "dayjs";

interface SixMonthGotExpectedChartProps {
    height?: number;
    style?: React.CSSProperties;
    accentColor?: string;
    showValues?: boolean;
}

type MonthKey = string;

interface SixMonthGotExpectedChartState {
    width: number;
    data: Record<MonthKey, { expected: number; received: number }>;
}

export default class SixMonthGotExpectedChart extends React.Component<SixMonthGotExpectedChartProps, SixMonthGotExpectedChartState> {
    static contextType = AuthContext;
    context!: AuthContextType;

    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: any) {
        super(props);
        this.state = { width: 640, data: {} };
    }

    public componentDidMount(): void {
        this.measure();
        this.getData();
        window.addEventListener("resize", this.measure);
    }

    public componentWillUnmount(): void {
        window.removeEventListener("resize", this.measure);
    }

    private measure = () => {
        const w = this.containerRef.current?.offsetWidth;
        if (w && Math.abs(w - this.state.width) > 8) {
            this.setState({ width: w });
        }
    };

    // ---------- utils ----------
    private toNumber = (v: any) => {
        if (typeof v === "number") return Number.isFinite(v) ? v : 0;
        if (typeof v === "string") {
            const s = v.replace(/\s/g, "").replace("€", "").replace(",", ".");
            const n = parseFloat(s);
            return Number.isFinite(n) ? n : 0;
        }
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    // ---------- data ----------
    private async getData() {
        const startMonth = dayjs().startOf("month").subtract(5, "month");
        const endMonth = dayjs().endOf("month");

        const data: Record<string, { expected: number; received: number }> = {};
        for (let i = 5; i >= 0; i--) {
            const monthKey = dayjs().subtract(i, "month").format("YYYY-MM");
            data[monthKey] = { expected: 0, received: 0 };
        }
        this.setState({ data });

        const gotExpectedResult = await TipJarRepository.calculateGotExpected(this.context.locationId, startMonth, endMonth);
        if (gotExpectedResult.isFailed()) return;

        const nextData = { ...data };
        for (const item of gotExpectedResult.result?.gotExpected ?? []) {
            const monthKey = dayjs(item.date).format("YYYY-MM");
            if (!nextData[monthKey]) continue;

            nextData[monthKey].received = this.toNumber(item.got);
            nextData[monthKey].expected = this.toNumber(item.expected);
        }

        this.setState({ data: nextData });
    }

    // ---------- colors ----------
    private getTheme() {
        const accent = (this.props.accentColor || "#c79656").trim();

        const hexToRgba = (hex: string, a: number) => {
            const h = hex.replace("#", "");
            if (h.length !== 6) return `rgba(199,150,86,${a})`;
            const r = parseInt(h.slice(0, 2), 16);
            const g = parseInt(h.slice(2, 4), 16);
            const b = parseInt(h.slice(4, 6), 16);
            return `rgba(${r},${g},${b},${a})`;
        };

        return {
            accent,
            expectedFill: "rgba(15, 23, 42, 0.22)",
            grid: "rgba(15, 23, 42, 0.10)",
            frame: "rgba(15, 23, 42, 0.12)",
            label: "rgba(15, 23, 42, 0.72)",
            labelMuted: "rgba(15, 23, 42, 0.52)",
            valueOnBar: "rgba(15, 23, 42, 0.82)",
            accentSoft: hexToRgba(accent, 0.12),
            accentMid: hexToRgba(accent, 0.25),
        };
    }

    // ---------- formatting & ticks ----------
    private chartEuro = (v: number) => {
        const abs = Math.abs(v);
        if (abs >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`;
        if (abs >= 1_000) return `€${(v / 1_000).toFixed(1)}k`;
        if (abs >= 100) return `€${v.toFixed(0)}`;
        if (abs >= 10) return `€${v.toFixed(0)}`;
        return `€${v.toFixed(2)}`;
    };

    private chartNumber = (range: number, round: boolean) => {
        const exp = Math.floor(Math.log10(range));
        const frac = range / Math.pow(10, exp);
        let niceFrac: number;

        if (round) {
            if (frac < 1.5) niceFrac = 1;
            else if (frac < 3) niceFrac = 2;
            else if (frac < 3.75) niceFrac = 2.5;
            else if (frac < 7.5) niceFrac = 5;
            else niceFrac = 10;
        } else {
            if (frac <= 1) niceFrac = 1;
            else if (frac <= 2) niceFrac = 2;
            else if (frac <= 2.5) niceFrac = 2.5;
            else if (frac <= 5) niceFrac = 5;
            else niceFrac = 10;
        }

        return niceFrac * Math.pow(10, exp);
    };

    private chartTicks = (maxVal: number, height: number) => {
        const padT = 12;
        const padB = 30;
        const h = Math.max(1, height - padT - padB);
        const desired = Math.min(7, Math.max(3, Math.round(h / 44)));
        const range = Math.max(1e-9, maxVal);
        const step = this.chartNumber(range / Math.max(1, desired - 1), true);
        const top = Math.ceil(maxVal / step) * step;

        const ticks: number[] = [];
        for (let v = 0; v <= top + 1e-9; v += step) ticks.push(Number(v.toFixed(12)));

        return { top, ticks, padT, padB, h };
    };

    // ---------- layout ----------
    private createChartBars = (width: number, height: number, top: number) => {
        const padYTop = 12;
        const padYBot = 30;
        const padXLeft = 54;
        const padXRight = 14;

        const w = Math.max(1, width - padXLeft - padXRight);
        const h = Math.max(1, height - padYTop - padYBot);

        const legendReserve = 28;
        const plotTop = padYTop + legendReserve;
        const hEff = Math.max(1, h - legendReserve);

        const months = Object.keys(this.state.data);
        const groupW = w / Math.max(1, months.length);

        const gap = Math.min(18, Math.max(10, groupW * 0.22));
        const barW = Math.max(6, (groupW - gap) / 2);

        const safeTop = Number.isFinite(top) && top > 0 ? top : 1;
        const scaleY = (v: number) => plotTop + (1 - v / safeTop) * hEff;

        const bars: {
            x: number;
            y: number;
            width: number;
            height: number;
            type: "got" | "expected";
            value: number;
            cx: number;
            cy: number;
            monthKey: string;
        }[] = [];

        months.forEach((key, i) => {
            const { expected, received } = this.state.data[key];
            const gx = padXLeft + i * groupW + gap / 2;

            const h1 = Math.max(0, (this.toNumber(received) / safeTop) * hEff);
            const h2 = Math.max(0, (this.toNumber(expected) / safeTop) * hEff);

            bars.push({
                x: gx,
                y: plotTop + (hEff - h1),
                width: barW,
                height: h1,
                type: "got",
                value: this.toNumber(received),
                cx: gx + barW / 2,
                cy: plotTop + (hEff - h1) - 7,
                monthKey: key,
            });

            bars.push({
                x: gx + barW + gap / 2,
                y: plotTop + (hEff - h2),
                width: barW,
                height: h2,
                type: "expected",
                value: this.toNumber(expected),
                cx: gx + barW + gap / 2 + barW / 2,
                cy: plotTop + (hEff - h2) - 7,
                monthKey: key,
            });
        });

        const labels = months.map((key, i) => ({
            x: padXLeft + i * groupW + groupW / 2,
            text: dayjs(`${key}-01`).format("MMM YY"),
            full: dayjs(`${key}-01`).format("MMMM YYYY"),
        }));

        return {
            bars,
            padXLeft,
            padXRight,
            padYTop,
            padYBot,
            w,
            h,
            labels,
            xLeft: padXLeft,
            xRight: padXLeft + w,
            plotTop,
            scaleY,
        };
    };

    render() {
        const height = this.props.height ?? 180;
        const theme = this.getTheme();

        const values = Object.values(this.state.data);
        const computedMax = Math.max(1, ...values.map((d) => Math.max(this.toNumber(d.received), this.toNumber(d.expected))));
        const maxVal = Number.isFinite(computedMax) && computedMax > 0 ? computedMax : 1;

        const { top, ticks, padT, h } = this.chartTicks(maxVal * 1.12, height);
        const layout = this.createChartBars(this.state.width, height, top);

        const showValues = Boolean(this.props.showValues);

        return (
            <div ref={this.containerRef} style={{ width: "100%", ...this.props.style }}>
                <svg width="100%" height={height} viewBox={`0 0 ${this.state.width} ${height}`} role="img">
                    <defs>
                        <linearGradient id="gotGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={theme.accent} stopOpacity={0.9} />
                            <stop offset="100%" stopColor={theme.accent} stopOpacity={0.45} />
                        </linearGradient>
                    </defs>

                    <rect x={layout.xLeft} y={padT} width={layout.w} height={h} rx={12} ry={12} fill="rgba(255,255,255,0.75)" stroke={theme.frame} />

                    {ticks.map((t, i) => {
                        const y = layout.scaleY(t);
                        return <line key={i} x1={layout.xLeft} y1={y} x2={layout.xRight} y2={y} stroke={theme.grid} strokeDasharray="4 6" />;
                    })}

                    {ticks.map((t, i) => {
                        const y = layout.scaleY(t);
                        return (
                            <text key={`y${i}`} x={layout.xLeft - 10} y={y + 4} textAnchor="end" fontSize="11" fill={theme.labelMuted}>
                                {this.chartEuro(t)}
                            </text>
                        );
                    })}

                    {layout.bars.map((b, i) => {
                        const fill = b.type === "got" ? "url(#gotGradient)" : theme.expectedFill;
                        const stroke = b.type === "got" ? theme.accentMid : "rgba(15, 23, 42, 0.12)";

                        return (
                            <g key={i}>
                                <rect x={b.x} y={b.y} width={b.width} height={b.height} rx={6} ry={6} fill={fill} stroke={stroke} strokeWidth={0.75}>
                                    <title>
                                        {dayjs(`${b.monthKey}-01`).format("MMMM YYYY")} — Pin tip {b.type === "got" ? "received" : "expected"}: {this.chartEuro(b.value)}
                                    </title>
                                </rect>

                                {showValues && b.height > 16 && (
                                    <text x={b.cx} y={b.cy} textAnchor="middle" fontSize="9.5" fill={theme.valueOnBar}>
                                        {this.chartEuro(b.value)}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {layout.labels.map((l, i) => (
                        <text key={i} x={l.x} y={height - 8} textAnchor="middle" fontSize="10.5" fill={theme.label}>
                            <title>{l.full}</title>
                            {l.text}
                        </text>
                    ))}

                    {(() => {
                        const legendW = 220;
                        const legendX = layout.xLeft + (layout.w - legendW) / 2;
                        const legendY = layout.padYTop + 8;

                        return (
                            <g transform={`translate(${legendX}, ${legendY})`}>
                                <rect x="0" y="0" width="86" height="18" rx="9" ry="9" fill={theme.accentSoft} stroke={theme.accentMid} />
                                <rect x="6" y="5" width="8" height="8" rx="2" ry="2" fill="url(#gotGradient)" />
                                <text x="20" y="13" fontSize="11.5" fill={theme.label}>
                                    Got
                                </text>

                                <rect x="104" y="0" width="106" height="18" rx="9" ry="9" fill="rgba(15, 23, 42, 0.06)" stroke="rgba(15, 23, 42, 0.12)" />
                                <rect x="110" y="5" width="8" height="8" rx="2" ry="2" fill="rgba(15, 23, 42, 0.24)" />
                                <text x="124" y="13" fontSize="11.5" fill={theme.label}>
                                    Expected
                                </text>
                            </g>
                        );
                    })()}
                </svg>
            </div>
        );
    }
}